import type { AxiosError } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";

import type {
  Moderator,
  PauseModeratorAccountHeadRequest,
  PauseModeratorAccountRequest,
} from "@/entities/moderator";
import { useApi } from "@/shared/api/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  PaginationRequest,
  PaginatedResponse,
  ApiResponse,
} from "@/shared/types";

export const useGetModerators = (
  props: PaginationRequest & { isBanned?: boolean; isMaster?: boolean },
) => {
  const { api } = useApi();
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<Moderator>>({
    queryKey: ["moderators", params],
    queryFn: async () => {
      const { data } = await api.get("/moderators", {
        params: params,
      });

      return data;
    },
  });
};

export const useGetModeratorById = ({ userId }: { userId: string }) => {
  const { api } = useApi();

  return useQuery<ApiResponse<Moderator>>({
    queryKey: ["moderator", userId],
    queryFn: async () => {
      const { data } = await api.get(`/moderators/user/${userId}`);

      return data;
    },
  });
};

export const usePauseModeratorAccount = () => {
  const { api } = useApi();

  return useMutation<void, AxiosError, PauseModeratorAccountRequest>({
    mutationKey: ["pauseModerator"],
    mutationFn: async (data) => {
      await api.patch("/moderators/pause", data);
    },
  });
};

export const usePauseModeratorAccountHead = () => {
  const { api } = useApi();

  return useMutation<void, AxiosError, PauseModeratorAccountHeadRequest>({
    mutationKey: ["pauseModeratorHead"],
    mutationFn: async ({ pauseStatus, id }) => {
      await api.patch(`/moderators/${id}`, { pauseStatus });
    },
  });
};
